/**
 * Analytics Factory — Orquestra GA4, Meta Pixel e Google Ads
 *
 * Design: "Adapter Pattern" — cada plataforma é um adapter.
 * A factory recebe um evento tipado e o envia para todas as plataformas ativas.
 *
 * Regra de ouro: eventos são DETERMINÍSTICOS. Nunca improvisar.
 * Se o evento não está em analytics-events.ts, não existe.
 */

import { config } from './config';
import { defaultLogger as log } from './logger';
import { sanitize } from './analytics/sanitize';
import { debug } from './analytics/debug';
import { enqueueEvent } from './analytics/serverProxy';
import type { AnalyticsEvent } from './analytics-events';

/* ═══════════════════════════════════════════
   Plataformas — Adapters
   ═══════════════════════════════════════════ */

interface AnalyticsAdapter {
  readonly name: string;
  readonly enabled: boolean;
  track(event: AnalyticsEvent): void;
}

/* ─── Google Analytics 4 ─── */

class GA4Adapter implements AnalyticsAdapter {
  readonly name = 'GA4';
  readonly enabled: boolean;

  constructor() {
    this.enabled = !!config.ga4Id && config.ga4Id !== 'G-XXXXXXXXXX';
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined') return;

    const cleanParams = sanitize(event.params as Record<string, unknown>);
    const { name } = event;

    // Server-side mode: envia via proxy (escapa de ad blockers)
    if (config.serverSide) {
      enqueueEvent(name, cleanParams);
      debug.event(name, cleanParams, { mode: 'server' });
      return;
    }

    const gtag = (window as any).gtag;
    if (!gtag) {
      debug.warn(`[GA4] gtag não disponível — evento "${name}" perdido`);
      return;
    }

    gtag('event', name, cleanParams);
    debug.event(name, cleanParams, { mode: 'client' });
  }
}

/* ─── Google Tag Manager (DataLayer) ─── */

class GTMAdapter implements AnalyticsAdapter {
  readonly name = 'GTM';
  readonly enabled: boolean;

  constructor() {
    this.enabled = !!config.gtmId && config.gtmId !== 'GTM-XXXXXXX';
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined') return;
    const dl = (window as any).dataLayer;
    if (!dl) return;

    const cleanParams = sanitize(event.params as Record<string, unknown>);
    dl.push({
      event: event.name,
      ...cleanParams,
    });

    debug.event(event.name, cleanParams, { mode: 'gtm' });
  }
}

/* ─── Meta Pixel (Facebook/Instagram) ─── */

class MetaPixelAdapter implements AnalyticsAdapter {
  readonly name = 'MetaPixel';
  readonly enabled: boolean;

  constructor() {
    this.enabled = !!config.metaPixelId && config.metaPixelId !== 'PIXEL-XXXXXXX';
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined') return;
    const fbq = (window as any).fbq;
    if (!fbq) return;

    const cleanParams = sanitize(event.params as Record<string, unknown>);
    const mapping = this.mapEvent({ ...event, params: cleanParams } as AnalyticsEvent);
    if (!mapping) return;

    fbq('track', mapping.eventName, mapping.params);
    debug.event(`meta:${mapping.eventName}`, mapping.params, { mode: 'meta' });
  }

  private mapEvent(event: AnalyticsEvent): { eventName: string; params: Record<string, unknown> } | null {
    const { name, params } = event;

    switch (name) {
      case 'page_view':
        return { eventName: 'PageView', params: {} };
      case 'search':
        return { eventName: 'Search', params: { search_string: (params as any).search_term } };
      case 'whatsapp_click':
        return { eventName: 'Contact', params: { content_name: (params as any).provider } };
      case 'booking_click':
        return { eventName: 'InitiateCheckout', params: { content_name: (params as any).entity_name, value: (params as any).price, currency: (params as any).currency || 'BRL' } };
      case 'purchase':
        return { eventName: 'Purchase', params: { value: (params as any).value, currency: (params as any).currency, content_ids: (params as any).items?.map((i: any) => i.item_id) } };
      case 'newsletter_signup':
        return { eventName: 'Lead', params: { content_name: 'newsletter' } };
      case 'generate_lead':
        return { eventName: 'Lead', params: { value: (params as any).value, currency: (params as any).currency } };
      case 'affiliate_click':
        return { eventName: 'InitiateCheckout', params: { content_name: (params as any).provider } };
      case 'sign_up':
        return { eventName: 'CompleteRegistration', params: { content_name: (params as any).method } };
      default:
        // Eventos customizados do Meta
        return { eventName: 'CustomEvent', params: { custom_event_name: name, ...params } };
    }
  }
}

/* ─── Google Ads — Conversion Tracking ─── */

class GoogleAdsAdapter implements AnalyticsAdapter {
  readonly name = 'GoogleAds';
  readonly enabled: boolean;

  constructor() {
    this.enabled = !!config.googleAdsId && config.googleAdsId !== 'AW-XXXXXXXXX';
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined') return;
    const gtag = (window as any).gtag;
    if (!gtag) return;

    const cleanParams = sanitize(event.params as Record<string, unknown>);
    const conversion = this.mapConversion({ ...event, params: cleanParams } as AnalyticsEvent);
    if (!conversion) return;

    gtag('event', 'conversion', {
      send_to: `${config.googleAdsId}/${conversion.label}`,
      value: conversion.value,
      currency: conversion.currency,
    });

    debug.event(`ads:${conversion.label}`, conversion, { mode: 'google_ads' });
  }

  private mapConversion(event: AnalyticsEvent): { label: string; value?: number; currency?: string } | null {
    const labels: Record<string, { label: string; valueKey?: string; currencyKey?: string }> = {
      whatsapp_click: { label: 'whatsapp_lead' },
      booking_click: { label: 'booking_initiated', valueKey: 'value', currencyKey: 'currency' },
      purchase: { label: 'purchase', valueKey: 'value', currencyKey: 'currency' },
      newsletter_signup: { label: 'newsletter_signup' },
      generate_lead: { label: 'lead_generated', valueKey: 'value', currencyKey: 'currency' },
      affiliate_click: { label: 'affiliate_click' },
      sign_up: { label: 'signup_complete' },
    };

    const mapping = labels[event.name];
    if (!mapping) return null;

    const params = event.params as Record<string, any>;
    return {
      label: mapping.label,
      value: mapping.valueKey ? params[mapping.valueKey] : undefined,
      currency: mapping.currencyKey ? params[mapping.currencyKey] : undefined,
    };
  }
}

/* ─── TikTok Pixel ─── */

class TikTokAdapter implements AnalyticsAdapter {
  readonly name = 'TikTok';
  readonly enabled: boolean;

  constructor() {
    this.enabled = !!config.tiktokPixelId && config.tiktokPixelId !== 'TIKTOK-XXXXXXX';
  }

  track(event: AnalyticsEvent) {
    if (typeof window === 'undefined') return;
    const ttq = (window as any).ttq;
    if (!ttq) return;

    const cleanParams = sanitize(event.params as Record<string, unknown>);
    const mapping = this.mapEvent({ ...event, params: cleanParams } as AnalyticsEvent);
    if (!mapping) return;

    ttq.track(mapping.eventName, mapping.params);
    debug.event(`tiktok:${mapping.eventName}`, mapping.params, { mode: 'tiktok' });
  }

  private mapEvent(event: AnalyticsEvent): { eventName: string; params: Record<string, unknown> } | null {
    const { name, params } = event;

    switch (name) {
      case 'page_view':
        return { eventName: 'Browse', params: {} };
      case 'search':
        return { eventName: 'Search', params: { query: (params as any).search_term } };
      case 'whatsapp_click':
      case 'booking_click':
      case 'generate_lead':
      case 'newsletter_signup':
        return { eventName: 'SubmitForm', params: { content_name: name } };
      case 'purchase':
        return { eventName: 'CompletePayment', params: { value: (params as any).value, currency: (params as any).currency } };
      case 'begin_checkout':
        return { eventName: 'InitiateCheckout', params: { value: (params as any).value, currency: (params as any).currency } };
      case 'view_item':
        return { eventName: 'ViewContent', params: { content_name: (params as any).items?.[0]?.item_name } };
      case 'sign_up':
        return { eventName: 'CompleteRegistration', params: { method: (params as any).method } };
      default:
        return { eventName: 'CustomEvent', params: { event_name: name, ...params } };
    }
  }
}

/* ═══════════════════════════════════════════
   Factory — Orquestra Todos os Adapters
   ═══════════════════════════════════════════ */

class AnalyticsFactory {
  private adapters: AnalyticsAdapter[];

  constructor() {
    this.adapters = [
      new GA4Adapter(),
      new GTMAdapter(),
      new MetaPixelAdapter(),
      new GoogleAdsAdapter(),
      new TikTokAdapter(),
    ].filter(a => a.enabled);

    if (config.isDev) {
      const active = this.adapters.map(a => a.name).join(', ');
      log.info(`[AnalyticsFactory] Plataformas ativas: ${active || 'NENHUMA'}`);
      if (config.serverSide) {
        log.info('[AnalyticsFactory] Server-side tracking ATIVO (via /api/collect)');
      }
    }
  }

  /**
   * Envia um evento para TODAS as plataformas ativas.
   * Nunca falha silenciosamente — loga em dev.
   */
  track(event: AnalyticsEvent) {
    if (this.adapters.length === 0) {
      if (config.isDev) {
        log.warn(`[AnalyticsFactory] Nenhuma plataforma ativa — evento "${event.name}" descartado`);
      }
      return;
    }

    for (const adapter of this.adapters) {
      try {
        adapter.track(event);
      } catch (err) {
        log.error(`[AnalyticsFactory] Erro em ${adapter.name}: ${err}`);
      }
    }
  }

  /**
   * Verifica se uma plataforma específica está ativa.
   */
  isActive(name: string): boolean {
    return this.adapters.some(a => a.name === name);
  }

  /**
   * Lista plataformas ativas.
   */
  get activePlatforms(): string[] {
    return this.adapters.map(a => a.name);
  }
}

/* Singleton */
export const analytics = new AnalyticsFactory();

/* ═══════════════════════════════════════════
   Helpers de Conveniência
   ═══════════════════════════════════════════ */

export function track(event: AnalyticsEvent) {
  analytics.track(event);
}
